import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ConfirmDialog,
  FieldError,
  IconButton,
} from "@scribenote/brian-design-system";
import { viewCaption, type ViewIdentity } from "@views/viewIdentity";
import { useDeleteView } from "@views/viewsQueries";
import { useState } from "react";

/**
 * The delete affordance for a single view: a trash icon that opens a confirm
 * dialog and runs the delete mutation. Shared by the in-panel detail and the
 * standalone view route. `onDeleted` fires after a successful delete so each
 * surface can close/navigate as it sees fit.
 */
export const ViewDeleteButton = ({
  view,
  onDeleted,
}: {
  view: ViewIdentity & { id: string };
  onDeleted: () => void;
}) => {
  const [confirming, setConfirming] = useState(false);
  const deleteView = useDeleteView();
  // Named in full here, unlike the surfaces that can lean on their surroundings:
  // this is clinic-wide and irreversible, so the one line the dialog gets has to
  // say which record it is about (BE-2395).
  const caption = viewCaption(view, { withSubject: true });

  return (
    <>
      <IconButton
        variant="outline"
        onClick={() => setConfirming(true)}
        aria-label="Delete view"
        data-testid={`view-delete-${view.id}`}
        className="text-ink-stopped"
      >
        <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
      </IconButton>

      <ConfirmDialog
        open={confirming}
        title="Delete this view?"
        confirmLabel="Delete"
        destructive
        busy={deleteView.isPending}
        onCancel={() => {
          deleteView.reset();
          setConfirming(false);
        }}
        onConfirm={() =>
          deleteView.mutate(view.id, {
            onSuccess: () => {
              setConfirming(false);
              onDeleted();
            },
          })
        }
        confirmTestId={`view-delete-confirm-${view.id}-confirm`}
        cancelTestId={`view-delete-confirm-${view.id}-cancel`}
      >
        <p className="text-sm text-ink-body">
          Deleting{" "}
          <strong className="font-medium text-ink">
            {[view.name, caption].filter(Boolean).join(" · ")}
          </strong>{" "}
          removes it for the whole clinic. This can&apos;t be undone.
        </p>
        {deleteView.isError && (
          <FieldError className="mt-2">
            Couldn’t delete this view. Try again.
          </FieldError>
        )}
      </ConfirmDialog>
    </>
  );
};
